import React, { Context, createContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgreementType, UserType } from 'api/types';

export interface AgreementTableUIState {
  page: number;
  pageSize: number;
  direction: 'ascending' | 'descending';
  sortBy: string;
  selectedStatuses: string[];
  selectedMilestones: string[];
  searchQuery: string;
}

export type AgreementTableAction =
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'SET_SORT'; payload: { sortBy: string; direction: 'ascending' | 'descending' } }
  | { type: 'SET_STATUS_FILTERS'; payload: string[] }
  | { type: 'SET_MILESTONE_FILTERS'; payload: string[] }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | {
      type: 'SYNC_FROM_URL';
      payload: { selectedStatuses: string[]; selectedMilestones: string[]; searchQuery: string };
    }
  | { type: 'RESET_FILTERS' }
  | { type: 'RESET_PAGINATION' };

export interface MonitoringTableState {
  goodAgreements: AgreementType[];
  badAgreements: AgreementType[];
  page: number;
  pageSize: number;
  count: number;
  totalPages: number;
}

export type MonitoringTableAction =
  | { type: 'SET_AGREEMENTS'; payload: { good: AgreementType[]; bad: AgreementType[] } }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number };

interface AdminContextType {
  agreementTableState: AgreementTableUIState;
  updateAgreementTable: (action: AgreementTableAction) => void;
  monitoringTableState: MonitoringTableState;
  updateMonitoringTable: (action: MonitoringTableAction) => void;
  internalUsers: UserType[];
  setInternalUsers: React.Dispatch<React.SetStateAction<UserType[]>>;
}

const AdminContext: Context<AdminContextType> = createContext({} as AdminContextType);

const parseUrlFilters = (searchParams: URLSearchParams) => {
  const statusParam = searchParams.get('status');
  const milestoneParam = searchParams.get('milestone');
  const searchParam = searchParams.get('search');

  return {
    selectedStatuses: statusParam ? statusParam.split(',').filter(Boolean) : [],
    selectedMilestones: milestoneParam ? milestoneParam.split(',').filter(Boolean) : [],
    searchQuery: searchParam || '',
  };
};

const updateUrlParams = (
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void,
  selectedStatuses: string[],
  selectedMilestones: string[],
  searchQuery: string,
) => {
  const newParams = new URLSearchParams(searchParams);

  if (selectedStatuses.length > 0) {
    newParams.set('status', selectedStatuses.join(','));
  } else {
    newParams.delete('status');
  }

  if (selectedMilestones.length > 0) {
    newParams.set('milestone', selectedMilestones.join(','));
  } else {
    newParams.delete('milestone');
  }

  if (searchQuery.trim()) {
    newParams.set('search', searchQuery);
  } else {
    newParams.delete('search');
  }

  setSearchParams(newParams);
};

const agreementTableReducer = (
  state: AgreementTableUIState,
  action: AgreementTableAction,
): AgreementTableUIState => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };

    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, page: 1 }; // Reset to first page

    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy,
        direction: action.payload.direction,
        page: 1, // Reset to first page when sorting
      };

    case 'SET_STATUS_FILTERS':
      return { ...state, selectedStatuses: action.payload, page: 1 };

    case 'SET_MILESTONE_FILTERS':
      return { ...state, selectedMilestones: action.payload, page: 1 };

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload, page: 1 };

    case 'SYNC_FROM_URL':
      return {
        ...state,
        selectedStatuses: action.payload.selectedStatuses,
        selectedMilestones: action.payload.selectedMilestones,
        searchQuery: action.payload.searchQuery,
        page: 1,
      };

    case 'RESET_FILTERS':
      return { ...state, selectedStatuses: [], selectedMilestones: [], page: 1 };

    case 'RESET_PAGINATION':
      return { ...state, page: 1 };

    default:
      return state;
  }
};

const monitoringTableReducer = (
  state: MonitoringTableState,
  action: MonitoringTableAction,
): MonitoringTableState => {
  switch (action.type) {
    case 'SET_AGREEMENTS':
      return {
        ...state,
        goodAgreements: action.payload.good,
        badAgreements: action.payload.bad,
        count: action.payload.good.length + action.payload.bad.length,
        totalPages: Math.ceil(
          (action.payload.good.length + action.payload.bad.length) / state.pageSize,
        ),
      };

    case 'SET_PAGE':
      return { ...state, page: action.payload };

    case 'SET_PAGE_SIZE':
      return {
        ...state,
        pageSize: action.payload,
        page: 1,
        totalPages: Math.ceil(state.count / action.payload),
      };

    default:
      return state;
  }
};

function AdminContextProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = parseUrlFilters(searchParams);

  const [agreementTableState, dispatchAgreementTable] = React.useReducer(agreementTableReducer, {
    page: 1,
    pageSize: 25,
    direction: 'ascending',
    sortBy: '',
    selectedStatuses: initialFilters.selectedStatuses,
    selectedMilestones: initialFilters.selectedMilestones,
    searchQuery: initialFilters.searchQuery,
  });

  const [monitoringTableState, dispatchMonitoringTable] = React.useReducer(monitoringTableReducer, {
    goodAgreements: [],
    badAgreements: [],
    page: 1,
    pageSize: 25,
    count: 0,
    totalPages: 0,
  });

  const [internalUsers, setInternalUsers] = useState<UserType[]>([]);

  useEffect(() => {
    updateUrlParams(
      searchParams,
      setSearchParams,
      agreementTableState.selectedStatuses,
      agreementTableState.selectedMilestones,
      agreementTableState.searchQuery,
    );
  }, [
    agreementTableState.selectedStatuses,
    agreementTableState.selectedMilestones,
    agreementTableState.searchQuery,
    searchParams,
    setSearchParams,
  ]);

  const updateAgreementTable = useCallback((action: AgreementTableAction) => {
    dispatchAgreementTable(action);
  }, []);

  const updateMonitoringTable = useCallback((action: MonitoringTableAction) => {
    dispatchMonitoringTable(action);
  }, []);

  const value = useMemo(
    () => ({
      agreementTableState,
      updateAgreementTable,
      monitoringTableState,
      updateMonitoringTable,
      internalUsers,
      setInternalUsers,
    }),
    [
      agreementTableState,
      updateAgreementTable,
      monitoringTableState,
      updateMonitoringTable,
      internalUsers,
    ],
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export { AdminContext, AdminContextProvider };
